import { useCallback, useEffect, useMemo, useState } from "react";
import { TranslationService } from "../../../services/TranslationService";
import ButtonPrimary from "../../account/components/ButtonPrimary";
import * as yup from "yup";
import CompanyService from "../../../services/CompanyService";
import { TitleConfig } from "./components/TitleConfig";
import { NumberInput } from "../../shared/components/Editors";

type ReceivablesAgingGraphScaleType = {
  initialValue: string;
  fullSize?: boolean;
  /**
   * Cuando se provee, se llama con el string normalizado (ej: "-90, -60, -30, 0, 30, 60, 90")
   * cada vez que cambia la escala. Útil para pantallas que guardan con otro endpoint.
   */
  onChange?: (nextValue: string) => void;
  /**
   * Cuando se provee, reemplaza el guardado por defecto (CompanyService.setConfigCompany)
   */
  onSave?: (nextValue: string) => void | Promise<void>;
  /**
   * Permite ocultar el botón interno de guardar (para usar un botón externo)
   */
  showSaveButton?: boolean;
  /**
   * Título opcional para reutilización (por defecto usa translate.ReceivablesAgingGraphScale)
   */
  title?: string;
};

export const ReceivablesAgingGraphScale = ({
  initialValue = "",
  fullSize = false,
  onChange,
  onSave,
  showSaveButton = true,
  title
}: ReceivablesAgingGraphScaleType) => {
  const [error, setError] = useState("");
  const [saveData, setSaveData] = useState(false);
  const [scale, setScale] = useState(
    initialValue
      .trim()
      .split(/\s*,\s*|\s+/)
      .filter(Boolean)
      .map(parseFloat)
  );

  const formData = useMemo(
    () =>
      scale.length && {
        days1: scale[0],
        days2: scale[1],
        days3: scale[2],
        days4: scale[3],
        days5: scale[4],
        days6: scale[5],
        days7: scale[6]
      },
    [scale]
  );

  const { translate } = TranslationService;

  const formDataSchema = yup.object({
    days1: yup
      .number()
      .required(translate.EnterAtLeastOnePositiveOneNegativeOneZero)
      .typeError(translate.EnterAtLeastOnePositiveOneNegativeOneZero),
    days2: yup
      .number()
      .required(translate.EnterAtLeastOnePositiveOneNegativeOneZero)
      .typeError(translate.EnterAtLeastOnePositiveOneNegativeOneZero),
    days3: yup
      .number()
      .required(translate.EnterAtLeastOnePositiveOneNegativeOneZero)
      .typeError(translate.EnterAtLeastOnePositiveOneNegativeOneZero),
    days4: yup
      .number()
      .required(translate.EnterAtLeastOnePositiveOneNegativeOneZero)
      .typeError(translate.EnterAtLeastOnePositiveOneNegativeOneZero),
    days5: yup
      .number()
      .required(translate.EnterAtLeastOnePositiveOneNegativeOneZero)
      .typeError(translate.EnterAtLeastOnePositiveOneNegativeOneZero),
    days6: yup
      .number()
      .required(translate.EnterAtLeastOnePositiveOneNegativeOneZero)
      .typeError(translate.EnterAtLeastOnePositiveOneNegativeOneZero),
    days7: yup
      .number()
      .required(translate.EnterAtLeastOnePositiveOneNegativeOneZero)
      .typeError(translate.EnterAtLeastOnePositiveOneNegativeOneZero)
  });

  const handleScaleInputChange = (newValue: any, index: number) => {
    setScale((prevArray) => {
      const newArray = [...prevArray];
      newArray[index] = newValue;
      return newArray;
    });
    setSaveData(true);
  };

  const validateValuesRequired = useCallback(() => {
    const auxScale = scale.map((el) => el.toString()).map(parseFloat);
    const min = Math.min(...auxScale) < 0;
    const max = Math.max(...auxScale) > 0;
    const zero = auxScale.includes(0);
    const ascen = auxScale.every(
      (num, index, array) => index === 0 || num >= array[index - 1]
    );
    return !(min && max && zero && ascen);
  }, [scale]);

  const saveValues = useCallback(() => {
    setSaveData(false);
    const nextValue = scale.join(", ");
    if (onSave) {
      onSave(nextValue);
      return;
    }
    CompanyService.setConfigCompany({ graphscales: nextValue });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale, onSave]);

  // Propagar cambios hacia afuera (si corresponde)
  useEffect(() => {
    if (!onChange) return;
    onChange(scale.join(", "));
  }, [scale, onChange]);

  useEffect(() => {
    if (validateValuesRequired()) {
      setError(translate.EnterAtLeastOnePositiveOneNegativeOneZero);
      return;
    } else {
      setError("");
    }

    formDataSchema
      .validate(formData)
      .then(() => {
        setError("");
        setSaveData(true);
      })
      .catch((err) => setError(err.errors[0]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  return (
    <div className={`${fullSize ? "" : "col-md-8"} mx-3`}>
      <TitleConfig title={title ?? translate.ReceivablesAgingGraphScale} />
      <div className="row align-items-center">
        {scale.map((scaleItem, index) => (
          <div key={index} className="col">
            <NumberInput
              allowDecimal
              className="form-control"
              value={
                Number.isFinite(Number(scaleItem)) ? String(scaleItem) : ""
              }
              onChange={({ target: { value } }) =>
                handleScaleInputChange(value, index)
              }
            />
          </div>
        ))}
        <div className="col">{translate.Days}</div>
      </div>
      <div className="d-flex justify-content-between align-items-center my-2">
        <div
          id="errorMsj"
          className="incorrect"
          style={{ color: "red", fontSize: "14px", margin: "10px" }}
        >
          {error}
        </div>
        {showSaveButton && saveData && !error && (
          <ButtonPrimary className="ml-auto p-2" onClick={saveValues}>
            {translate.Save}
          </ButtonPrimary>
        )}
      </div>
    </div>
  );
};
